/**
 * Marks a live browser/tab session. Cleared automatically when the browser
 * (or tab) is closed because it lives in sessionStorage — not localStorage.
 * Refresh keeps sessionStorage, so the session survives F5 / pull-to-refresh.
 */
const BROWSER_SESSION_KEY = "browserSessionAlive";
const BACKGROUND_AT_KEY = "appBackgroundAt";
const VOLUNTARY_LOGOUT_KEY = "voluntaryLogout";
/** Survives tab close — next cold start should show Session expired once. */
const SESSION_EXPIRED_PENDING_KEY = "sessionExpiredPending";
/**
 * Set when the user dismisses the expired modal / goes to login.
 * Lives in sessionStorage so a real browser close clears it and the modal can show again.
 */
const SESSION_EXPIRED_ACK_KEY = "sessionExpiredAck";
const BROWSER_CLOSED_KEY = "browserSessionClosed";

/** Session expires after this long in background (switch apps / minimize). */
export const BACKGROUND_EXPIRE_MS = 30 * 60 * 1000;

export const markBrowserSessionAlive = (): void => {
  try {
    sessionStorage.setItem(BROWSER_SESSION_KEY, "1");
    sessionStorage.removeItem(VOLUNTARY_LOGOUT_KEY);
    sessionStorage.removeItem(SESSION_EXPIRED_ACK_KEY);
    localStorage.removeItem(BROWSER_CLOSED_KEY);
    localStorage.removeItem(SESSION_EXPIRED_PENDING_KEY);
  } catch {
    // ignore private mode / quota
  }
};

export const clearBrowserSessionAlive = (): void => {
  try {
    sessionStorage.removeItem(BROWSER_SESSION_KEY);
  } catch {
    // ignore
  }
};

export const hasBrowserSessionAlive = (): boolean => {
  try {
    return sessionStorage.getItem(BROWSER_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearBrowserClosed = (): void => {
  try {
    localStorage.removeItem(BROWSER_CLOSED_KEY);
  } catch {
    // ignore
  }
};

export const markSessionExpiredPending = (): void => {
  try {
    localStorage.setItem(SESSION_EXPIRED_PENDING_KEY, "1");
  } catch {
    // ignore
  }
};

export const isSessionExpiredPending = (): boolean => {
  try {
    return localStorage.getItem(SESSION_EXPIRED_PENDING_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearSessionExpiredPending = (): void => {
  try {
    localStorage.removeItem(SESSION_EXPIRED_PENDING_KEY);
  } catch {
    // ignore
  }
};

/** User saw the modal / went to login — do not show again until browser is closed. */
export const markSessionExpiredAck = (): void => {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_ACK_KEY, "1");
    localStorage.removeItem(SESSION_EXPIRED_PENDING_KEY);
  } catch {
    // ignore
  }
};

export const isSessionExpiredAck = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_ACK_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearSessionExpiredAck = (): void => {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_ACK_KEY);
  } catch {
    // ignore
  }
};

/** User chose Log out — SessionResumeGuard must not show "Session expired". */
export const markVoluntaryLogout = (): void => {
  try {
    sessionStorage.setItem(VOLUNTARY_LOGOUT_KEY, "1");
    sessionStorage.setItem(SESSION_EXPIRED_ACK_KEY, "1");
    localStorage.removeItem(BROWSER_CLOSED_KEY);
    localStorage.removeItem(SESSION_EXPIRED_PENDING_KEY);
  } catch {
    // ignore
  }
};

export const clearVoluntaryLogout = (): void => {
  try {
    sessionStorage.removeItem(VOLUNTARY_LOGOUT_KEY);
  } catch {
    // ignore
  }
};

export const isVoluntaryLogout = (): boolean => {
  try {
    return sessionStorage.getItem(VOLUNTARY_LOGOUT_KEY) === "1";
  } catch {
    return false;
  }
};

export const markAppBackgrounded = (): void => {
  try {
    localStorage.setItem(BACKGROUND_AT_KEY, Date.now().toString());
  } catch {
    // ignore
  }
};

export const clearAppBackgrounded = (): void => {
  try {
    localStorage.removeItem(BACKGROUND_AT_KEY);
  } catch {
    // ignore
  }
};

export const getAppBackgroundedAt = (): number => {
  try {
    return parseInt(localStorage.getItem(BACKGROUND_AT_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
};

/** True if the app was backgrounded longer than BACKGROUND_EXPIRE_MS. */
export const isBackgroundSessionExpired = (): boolean => {
  const backgroundAt = getAppBackgroundedAt();
  if (!backgroundAt) return false;
  return Date.now() - backgroundAt > BACKGROUND_EXPIRE_MS;
};
